/**
 * Magniberg collection navigation (blocks/_mb-collection-nav.liquid).
 *
 * Opens and closes the product filter panel. Filtering itself is handled by Horizon's
 * <facets-form-component>. The element, its toggle and its panel carry
 * `data-skip-node-update`, so the open state survives Horizon's section re-render
 * after each filter change.
 */
class MbCollectionNav extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get toggle() {
    return this.querySelector('.mb-collection-nav__toggle');
  }

  get panel() {
    return this.querySelector('.mb-collection-nav__mask');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    this.#setOpen(true);
  }

  close() {
    this.#setOpen(false);
  }

  /** @param {boolean} open */
  #setOpen(open) {
    this.toggleAttribute('open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));
    if (this.panel) this.panel.inert = !open;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.mb-collection-nav__toggle')) {
      this.#setOpen(!this.isOpen);
    } else if (event.target.closest('.mb-collection-nav__overlay')) {
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close();
    this.toggle?.focus();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
  };
}

if (!customElements.get('mb-collection-nav')) {
  customElements.define('mb-collection-nav', MbCollectionNav);
}
